import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { THEME, shadows } from '../constants/theme';
import InteractionDetails from '../components/InteractionDetails';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

export default function QuoteInteractionScreen() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchQuote();
  }, [id]);

  const fetchQuote = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('quotes')
        .select(`
          *,
          clients (
            client_name,
            client_email
          ),
          public_quotes (
            id
          )
        `)
        .eq('quote_id', id)
        .single();

      if (error) throw error;
      if (!data) throw new Error('Quote not found');

      setQuote(data);
    } catch (error) {
      console.error('Error fetching quote:', error);
      setError('Failed to load quote details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent" style={{ borderColor: `${THEME.primary} transparent` }} />
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="bg-white rounded-xl p-8 text-center max-w-md mx-auto" style={{ boxShadow: shadows.lg }}>
          <h1 className="text-2xl font-bold mb-2" style={{ color: THEME.error }}>
            Error Loading Quote
          </h1>
          <p style={{ color: THEME.subtext }}>
            {error || 'Quote not found'}
          </p>
          <Link
            to="/quotes"
            className="inline-flex items-center mt-4 text-sm"
            style={{ color: THEME.primary }}
          >
            <ArrowLeftIcon className="w-4 h-4 mr-1" />
            Back to Quotes
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-4">
          <Link
            to="/quotes"
            className="p-2 rounded-lg transition-colors duration-200 hover:bg-gray-100"
          >
            <ArrowLeftIcon className="w-5 h-5" style={{ color: THEME.primary }} />
          </Link>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: THEME.text }}>
              Quote #{quote.quote_number} Interactions
            </h1>
            <p className="text-sm mt-1" style={{ color: THEME.subtext }}>
              Client: {quote.clients.client_name}
            </p>
          </div>
        </div>
      </div>

      {/* Interaction Details */}
      {quote.public_quotes?.[0]?.id ? (
        <InteractionDetails
          publicLinkId={quote.public_quotes[0].id}
          linkType="quote"
        />
      ) : (
        <div className="bg-white rounded-xl p-6 text-center" style={{ boxShadow: shadows.md }}>
          <p style={{ color: THEME.subtext }}>
            This quote hasn't been shared with the client yet.
          </p>
        </div>
      )}
    </div>
  );
} 